import { useEffect, useMemo, useState } from 'react'
import type { Question, SessionResult, StudySupport } from '../types'
import { submitResult } from '../services/api'
import { removePendingResult, savePendingResult } from '../services/pendingResults'
import { downloadResultImage } from '../services/resultImage'
import { formatDuration } from '../utils/session'

interface Props {
  result: SessionResult
  questions: Question[]
  support?: StudySupport
  onReview: () => void
  onFinish: () => void
  onNextSession?: () => void
}

function fallbackIdeas(summary: string): string[] {
  return summary.split(/(?<=[.!?])\s+/).map((item) => item.trim()).filter(Boolean).slice(0, 4)
}

export function ResultsScreen({ result, questions, support, onReview, onFinish, onNextSession }: Props) {
  const [sendStatus, setSendStatus] = useState<'sending' | 'sent' | 'pending'>('sending')
  useEffect(() => {
    let active = true
    savePendingResult(result)
    submitResult(result).then(() => {
      removePendingResult(result.sessionId)
      if (active) setSendStatus('sent')
    }).catch(() => {
      if (active) setSendStatus('pending')
    })
    return () => { active = false }
  }, [result])

  const weakTopics = useMemo(() => [...new Set(result.questionResults
    .filter((item) => item.correct === false)
    .map((item) => questions.find((question) => question.id === item.questionId)?.topic)
    .filter((item): item is string => Boolean(item)))], [questions, result.questionResults])
  const strengths = Object.entries(result.resultsByOA).filter(([, item]) => item.percentage >= 70).map(([oa]) => oa)
  const keyIdeas = support?.keyIdeas.length ? support.keyIdeas : fallbackIdeas(support?.summary ?? '')

  return (
    <main id="main-content" className="results-page v3-results">
      <section className="results-hero learning-results">
        <div>
          <p className="eyebrow">Sesión finalizada</p>
          <h1>Buen trabajo, {result.student.firstName}</h1>
          <p>{result.student.message}</p>
          <div className="result-metrics"><span>{result.correctCount} respuestas correctas</span><span>{result.incorrectCount} contenidos por reforzar</span><span>{formatDuration(result.durationSeconds)}</span></div>
        </div>
        <div className="secondary-percentage" aria-label={`${result.percentage} por ciento`}><small>Resultado general</small><strong>{result.percentage}%</strong></div>
      </section>

      <section className="priority-result">
        <p className="step-label">Tu prioridad de estudio</p>
        <h2>{result.weakestTopic || 'Organización de lo aprendido'}</h2>
        <p>{result.recommendation}</p>
        {weakTopics.length > 1 && <p><strong>También conviene revisar:</strong> {weakTopics.join(', ')}.</p>}
      </section>

      <section className="learning-overview">
        <article className="learning-card"><h2>Lo que demostraste</h2><p>{strengths.length ? `Obtuviste buenos resultados en ${strengths.join(', ')}.` : 'Completaste la sesión y utilizaste la retroalimentación para seguir aprendiendo.'}</p></article>
        <article className="learning-card"><h2>Resultados por OA</h2><div className="oa-bars">{Object.entries(result.resultsByOA).map(([oa, item]) => <div key={oa}><span>{oa}</span><div><i style={{ width: `${item.percentage}%` }} /></div><strong>{item.percentage}%</strong></div>)}</div></article>
      </section>

      {support && (
        <section className="study-support-card v3-support">
          <div className="support-copy">
            <p className="eyebrow">Ficha de refuerzo · 10 a 15 minutos</p>
            <h2>{support.title}</h2>
            <p className="support-summary">{support.summary}</p>
            {support.notes.length > 0 && <section className="support-notes"><h3>Apuntes para estudiar</h3>{support.notes.map((note) => <p key={note}>{note}</p>)}</section>}
            <h3>Cuatro ideas importantes</h3>
            <ol className="key-ideas">{keyIdeas.map((idea) => <li key={idea}>{idea}</li>)}</ol>
            <h3>Conceptos clave</h3>
            <div className="concept-chips">{support.keyConcepts.map((concept) => <span key={concept}>{concept}</span>)}</div>
            {support.historicalExample && <p className="historical-example"><strong>Ejemplo histórico:</strong> {support.historicalExample}</p>}
            {support.tableHeaders.length > 0 && support.tableRows.length > 0 && <section className="support-table"><h3>{support.tableTitle || 'Tabla de apoyo'}</h3><div className="table-wrapper"><table><thead><tr>{support.tableHeaders.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{support.tableRows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}</tr>)}</tbody></table></div></section>}
          </div>
          {support.imagePath && <img src={support.imagePath} alt={`Apoyo visual sobre ${support.title}`} />}
          <div className="notebook-task structured-task">
            <h3>Trabajo en tu cuaderno</h3>
            <ol>
              {(support.notebookPrompts.length ? support.notebookPrompts : [
                'Escribe el título del contenido.',
                'Anota cuatro ideas importantes.',
                'Define dos conceptos históricos.',
                'Explica una relación de causa y consecuencia.',
                'Escribe una duda que todavía tengas.',
              ]).map((prompt) => <li key={prompt}>{prompt}</li>)}
            </ol>
            {(support.notebookActivity || result.config.notebookActivity) && <p><strong>Indicación especial de esta sesión:</strong> {support.notebookActivity || result.config.notebookActivity}</p>}
            {support.selfAssessment.length > 0 && <div className="support-self-assessment"><h4>Autoevaluación</h4>{support.selfAssessment.map((item) => <label key={item}><input type="checkbox" /> {item}</label>)}</div>}
            <p className="teacher-check"><strong>Cuando termines, muestra tu cuaderno a la profesora.</strong></p>
          </div>
        </section>
      )}

      {!support && <section className="notebook-task standalone structured-task"><h2>Trabajo en tu cuaderno</h2><ol><li>Escribe un título.</li><li>Anota cuatro ideas importantes.</li><li>Define dos conceptos.</li><li>Explica una causa y una consecuencia.</li><li>Escribe una duda.</li></ol><p>{result.config.notebookActivity}</p></section>}

      <p className={`send-status ${sendStatus}`} role="status">
        {sendStatus === 'sending' && 'Guardando tu resultado…'}
        {sendStatus === 'sent' && 'Resultado guardado correctamente.'}
        {sendStatus === 'pending' && 'No hubo conexión. El resultado quedó guardado en este computador para reintentar después.'}
      </p>

      {result.config.completionAction === 'teacher_authorization' && <p className="paper-note">La siguiente sesión requiere autorización. Termina el trabajo del cuaderno y espera la indicación de tu profesora.</p>}
      <div className="button-row results-actions">
        <button className="primary-button" type="button" onClick={onReview} disabled={result.incorrectIds.length === 0}>Repasar mis errores</button>
        <button className="secondary-button" type="button" onClick={() => downloadResultImage(result)}>Descargar resultado</button>
        {result.config.completionAction === 'allow_next' && onNextSession
          ? <button className="secondary-button" type="button" onClick={onNextSession}>Continuar con la siguiente sesión</button>
          : <button className="secondary-button" type="button" onClick={onFinish}>Finalizar sesión</button>}
      </div>
    </main>
  )
}
